import { getUnixTime } from "date-fns";
import { useEffect, useEffectEvent, useRef, useState } from "react";

import {
  getCurrentJwt,
  getSignInPageRedirect,
} from "#app/(unauthorized)/authentication/jwt";
import { useJwtLogout } from "#app/(unauthorized)/authentication/jwt-hooks";
import { Segment } from "#app/(unauthorized)/authentication/segment";
import { ENV } from "#app/env";
import {
  createActivityTimer,
  killTimer,
  resetTimer,
  startTimer,
} from "#app/lib/activityTimer";
import { TimeSpan } from "#app/lib/TimeSpan";
import { useFeatureToggles } from "#app/lib/useFeatureToggles";
import { localStorageKeys, useLocalStorage } from "#app/lib/useLocalStorage";

export const activityTimerName = "Inactivity";
const EVENTS_TO_RESET_TIMER = [
  "mousemove",
  "keydown",
  "wheel",
  "DOMMouseScroll",
  "mousewheel",
  "mousedown",
  "touchstart",
  "touchmove",
  "MSPointerDown",
  "MSPointerMove",
];

const DOCUMENT_EVENTS_TO_CHECK_EXPIRED_JWT = ["visibilitychange"];
// focus and pageshow are fired at window and never reach document listeners.
const WINDOW_EVENTS_TO_CHECK_EXPIRED_JWT = ["focus", "pageshow"];

export function useInactivityTimer() {
  const logout = useJwtLogout();
  const [result, setResult] = useState<boolean>(true);
  const [date, setDate] = useLocalStorage<number>(
    localStorageKeys.lastActivityTimestamp,
    getUnixTime(new Date()),
  );
  const lastDate = useRef<number>(getUnixTime(new Date()));
  const { data: featureToggles } = useFeatureToggles();
  const timeout = featureToggles?.["TimeoutAdjustmentFeature"]?.value
    ? Number(featureToggles["TimeoutAdjustmentFeature"].value)
    : ENV.autoLogOffIdleTime;
  const onTrigger = useEffectEvent(async (_: any) => {
    const result = !!date && date + timeout > getUnixTime(new Date());
    setResult(result);
    return result;
  });

  const resetInactivityTimer = useEffectEvent((newDate: number) => {
    // Prevents the timer from being reset if already reset within 500ms, which can happen when multiple events are fired in quick succession.
    if (newDate - lastDate.current < 0.5) {
      return;
    }

    const jwt = getCurrentJwt();
    const isLoggedIn = jwt.segment !== Segment.NotLoggedIn;
    const isJwtExpired = getCurrentJwt().isExpired();
    if (isLoggedIn && isJwtExpired) {
      // Advance lastDate so the date-sync effect does not trigger a second logout.
      lastDate.current = newDate;
      const signRedirectUrl = getSignInPageRedirect({ sessionExpired: true });
      void logout(signRedirectUrl);
      return;
    }

    lastDate.current = newDate;
    setResult(true);
    resetTimer(activityTimerName, {});
  });

  const resetTimerCallback = useEffectEvent(() => {
    const newDate = getUnixTime(new Date());
    setDate(newDate);
    resetInactivityTimer(newDate);
  });

  const checkExpiredJwtCallback = useEffectEvent(() => {
    // Ignore the visibilitychange fired when the tab is hidden.
    if (document.visibilityState === "hidden") {
      return;
    }

    const jwt = getCurrentJwt();
    if (jwt.segment !== Segment.NotLoggedIn && jwt.isExpired()) {
      void logout(getSignInPageRedirect({ sessionExpired: true }));
    }
  });

  useEffect(() => {
    if (date && date !== lastDate.current) {
      resetInactivityTimer(date);
    }
  }, [date]);

  useEffect(() => {
    // Kill the existing timer first to ensure we start fresh after calculating the correct timeout value.
    killTimer(activityTimerName);

    createActivityTimer({
      name: activityTimerName,
      getDelay: (_: any) => TimeSpan.fromSeconds(timeout),
      onTrigger: onTrigger,
      // TODO: TPD - evaluate if we need to handle failed
      // oxlint-disable-next-line no-empty-function
      failed: async (_: any) => {},
      // TODO: TPD - evaluate if we need to handle stopped
      // oxlint-disable-next-line no-empty-function
      stopped: () => {},
    });

    startTimer(activityTimerName, {});

    for (const event of EVENTS_TO_RESET_TIMER) {
      document.addEventListener(event, resetTimerCallback, false);
    }
    for (const event of DOCUMENT_EVENTS_TO_CHECK_EXPIRED_JWT) {
      document.addEventListener(event, checkExpiredJwtCallback, false);
    }
    for (const event of WINDOW_EVENTS_TO_CHECK_EXPIRED_JWT) {
      window.addEventListener(event, checkExpiredJwtCallback, false);
    }

    return () => {
      killTimer(activityTimerName);
      for (const event of EVENTS_TO_RESET_TIMER) {
        document.removeEventListener(event, resetTimerCallback);
      }
      for (const event of DOCUMENT_EVENTS_TO_CHECK_EXPIRED_JWT) {
        document.removeEventListener(event, checkExpiredJwtCallback);
      }
      for (const event of WINDOW_EVENTS_TO_CHECK_EXPIRED_JWT) {
        window.removeEventListener(event, checkExpiredJwtCallback);
      }
    };
  }, [timeout]);

  return { status: result };
}
